import React, { useState, useCallback, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import SignatureCanvas from "../../components/signature/SignatureCanvas";
import { signContract } from "../../services";
import {
  Button,
  Alert,
  Spinner,
  CheckboxWrapper,
} from "../../styles/SharedStyles";
import { TEXTS } from "../../constants";
import logoSvg from "../../assets/dark-logo.svg";
import {
  ContractContainer,
  ContractHeader,
  Logo,
  TimeRemainingBadge,
  SummaryBox,
  SummaryGrid,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ScrollHint,
  ContractContentWrapper,
  ContractContent,
  SigningSection,
  CheckboxesContainer,
  CheckboxesTitle,
  OptionalTitle,
  SignatureContainer,
  SignatureLabel,
  SubmitSection,
  ValidationMessage,
} from "./SignPage.styles";

function ContractView({ contract, onSigned, readOnly = false }) {
  const [checkboxStates, setCheckboxStates] = useState({});
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedContentHtml = useMemo(() => {
    if (!contract?.contentHtml) return "";
    
    return DOMPurify.sanitize(contract.contentHtml, {
      ALLOWED_TAGS: [
        "div", "span", "p", "h1", "h2", "h3", "h4", "h5", "h6",
        "table", "thead", "tbody", "tr", "td", "th",
        "strong", "em", "b", "i", "u", "br", "hr",
        "ul", "li", "ol", "a", "img",
        "blockquote", "pre", "code",
        "section", "article", "header", "footer",
      ],
      ALLOWED_ATTR: ["style", "class", "id", "href", "src", "alt", "title", "width", "height"],
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "textarea", "select"],
      FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover", "onmouseout", "onfocus", "onblur"],
      ALLOW_DATA_ATTR: false,
    });
  }, [contract?.contentHtml]);

  // Calculate time remaining
  useEffect(() => {
    if (!contract?.expiresAt) return;

    const calculateTimeRemaining = () => {
      const now = new Date();
      const expiresAt = new Date(contract.expiresAt);
      const diff = expiresAt - now;

      if (diff <= 0) {
        setTimeRemaining({ expired: true });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeRemaining({
        hours,
        minutes,
        totalHours: diff / (1000 * 60 * 60),
        expired: false,
      });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [contract?.expiresAt]);

  // Get time remaining class (for styling)
  const getTimeClass = () => {
    if (!timeRemaining || timeRemaining.expired) return "danger";
    if (timeRemaining.totalHours <= 6) return "danger";
    if (timeRemaining.totalHours <= 24) return "warning";
    return "";
  };

  // Format time remaining text
  const formatTimeRemaining = () => {
    if (!timeRemaining) return "";
    if (timeRemaining.expired) return "פג תוקף";

    if (timeRemaining.hours >= 24) {
      const days = Math.floor(timeRemaining.hours / 24);
      const remainingHours = timeRemaining.hours % 24;
      return `${days} ימים ו-${remainingHours} שעות`;
    }

    if (timeRemaining.hours > 0) {
      return `${timeRemaining.hours} שעות ו-${timeRemaining.minutes} דקות`;
    }

    return `${timeRemaining.minutes} דקות`;
  };

  // Handle checkbox change
  const handleCheckboxChange = (id) => {
    setCheckboxStates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Handle signature capture
  const handleSignatureEnd = useCallback((signatureDataUrl) => {
    setSignature(signatureDataUrl);
  }, []);

  // Clear signature
  const handleClearSignature = useCallback(() => {
    setSignature(null);
  }, []);

  // Check if all required checkboxes are checked
  const allRequiredChecked =
    contract.requiredCheckboxes?.every((cb) => checkboxStates[cb.id]) ?? true;

  // Check if signature exists
  const hasSignature = !!signature;

  // Can submit?
  const canSubmit = allRequiredChecked && hasSignature && !loading;

  // Handle submit
  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setLoading(true);
      setError(null);

      const response = await signContract(signature, checkboxStates);

      onSigned(response.data);
    } catch (err) {
      setError(err.message || TEXTS.CONTRACT.ERRORS.SIGN_FAILED);
      console.error("Sign error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = "ILS") => {
    if (!amount) return "-";
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency,
    }).format(amount);
  };

  return (
    <ContractContainer>
      {/* Header */}
      <ContractHeader>
        <Logo src={logoSvg} alt="DevCraft" />
        <h1>{TEXTS.CONTRACT.TITLE}</h1>
        <p>
          {TEXTS.CONTRACT.GREETING} {contract.clientName}
        </p>
      </ContractHeader>

      {/* Time Remaining Badge - positioned on the right */}
      {timeRemaining && !timeRemaining.expired && (
        <TimeRemainingBadge className={getTimeClass()}>
          <span className="icon">⏱️</span>
          <span>נותרו {formatTimeRemaining()} לחתימה</span>
        </TimeRemainingBadge>
      )}

      {/* Summary Box - only show if there's actual data */}
      {contract.serviceDetails &&
        (contract.serviceDetails.serviceName ||
          contract.serviceDetails.price ||
          contract.serviceDetails.startDate) && (
          <SummaryBox>
            <SummaryGrid>
              {contract.serviceDetails.serviceName && (
                <SummaryItem>
                  <SummaryLabel>{TEXTS.CONTRACT.SUMMARY.SERVICE}</SummaryLabel>
                  <SummaryValue>
                    {contract.serviceDetails.serviceName}
                  </SummaryValue>
                </SummaryItem>
              )}
              {contract.serviceDetails.price && (
                <SummaryItem>
                  <SummaryLabel>{TEXTS.CONTRACT.SUMMARY.PRICE}</SummaryLabel>
                  <SummaryValue>
                    {formatCurrency(
                      contract.serviceDetails.price,
                      contract.serviceDetails.currency
                    )}
                  </SummaryValue>
                </SummaryItem>
              )}
              {contract.serviceDetails.startDate && (
                <SummaryItem>
                  <SummaryLabel>
                    {TEXTS.CONTRACT.SUMMARY.START_DATE}
                  </SummaryLabel>
                  <SummaryValue>
                    {new Date(
                      contract.serviceDetails.startDate
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </SummaryValue>
                </SummaryItem>
              )}
            </SummaryGrid>
          </SummaryBox>
        )}

      {/* Scroll Hint - hide in read-only admin view */}
      {!readOnly && <ScrollHint>{TEXTS.CONTRACT.SCROLL_HINT}</ScrollHint>}

      {/* Contract Content */}
      <ContractContentWrapper>
        <ContractContent
          dangerouslySetInnerHTML={{ __html: sanitizedContentHtml }}
        />

        {readOnly && (
          <p style={{ textAlign: "center", color: "#64748B", marginTop: 24 }}>
            תצוגה בלבד (לינק ניהול)
          </p>
        )}

        {/* Signing Section - hidden in read-only */}
        {!readOnly && (
        <SigningSection>
          {/* Checkboxes */}
          <CheckboxesContainer>
            <CheckboxesTitle>{TEXTS.CONTRACT.CHECKBOXES.TITLE}</CheckboxesTitle>

            {/* Required Checkboxes */}
            {contract.requiredCheckboxes?.map((checkbox) => (
              <CheckboxWrapper key={checkbox.id}>
                <input
                  type="checkbox"
                  id={checkbox.id}
                  checked={!!checkboxStates[checkbox.id]}
                  onChange={() => handleCheckboxChange(checkbox.id)}
                />
                <label htmlFor={checkbox.id}>
                  {checkbox.label}{" "}
                  <span style={{ color: "#EF4444" }}>
                    {TEXTS.COMMON.REQUIRED_FIELD}
                  </span>
                </label>
              </CheckboxWrapper>
            ))}

            {/* Optional Checkboxes */}
            {contract.optionalCheckboxes?.length > 0 && (
              <>
                <OptionalTitle>
                  {TEXTS.CONTRACT.CHECKBOXES.OPTIONAL_TITLE}
                </OptionalTitle>
                {contract.optionalCheckboxes.map((checkbox) => (
                  <CheckboxWrapper key={checkbox.id}>
                    <input
                      type="checkbox"
                      id={checkbox.id}
                      checked={!!checkboxStates[checkbox.id]}
                      onChange={() => handleCheckboxChange(checkbox.id)}
                    />
                    <label htmlFor={checkbox.id}>{checkbox.label}</label>
                  </CheckboxWrapper>
                ))}
              </>
            )}
          </CheckboxesContainer>

          {/* Signature */}
          <SignatureContainer>
            <SignatureLabel>{TEXTS.CONTRACT.SIGNATURE.TITLE}</SignatureLabel>
            <SignatureCanvas
              onEnd={handleSignatureEnd}
              onClear={handleClearSignature}
            />
          </SignatureContainer>

          {/* Error */}
          {error && <Alert variant="error">{error}</Alert>}

          {/* Submit */}
          <SubmitSection>
            <Button
              variant="primary"
              $block
              $large
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {loading ? (
                <>
                  <Spinner size="20px" color="white" />
                  {TEXTS.CONTRACT.SUBMIT.SIGNING}
                </>
              ) : (
                <>
                  <span style={{ fontSize: "1.25rem" }}>✍️</span>
                  {TEXTS.CONTRACT.SUBMIT.BUTTON}
                </>
              )}
            </Button>

            {!allRequiredChecked && (
              <ValidationMessage>
                {TEXTS.CONTRACT.VALIDATION.CHECKBOXES_REQUIRED}
              </ValidationMessage>
            )}

            {allRequiredChecked && !hasSignature && (
              <ValidationMessage>
                {TEXTS.CONTRACT.VALIDATION.SIGNATURE_REQUIRED}
              </ValidationMessage>
            )}
          </SubmitSection>
        </SigningSection>
        )}
      </ContractContentWrapper>
    </ContractContainer>
  );
}

export default ContractView;
